import { TFileId } from 'types';
import { postMediaFile } from 'backend/additionalInformation';
import React, { useReducer } from 'react';
import { FormImageUploaderNewActionTypes } from './formImageUploaderNewActions';
import {
	FormImageUploaderNewContext,
	formImageUploaderNewInitialState,
	IPendingMedia,
	TImageOrientation,
	TTypeOfErrorModalWindow,
	TTypeOfModalWindow,
} from './formImageUploaderNewContext';
import { formImageUploaderNewReducer } from './formImageUploaderNewReducer';

interface IProps {
	setFileId: (fileId: TFileId) => void;
	setVideoFileId?: (fileId: TFileId) => void;
}

export const FormImageUploaderNewState: React.FC<IProps> = ({ children, setFileId, setVideoFileId }) => {
	const [state, dispatch] = useReducer(formImageUploaderNewReducer, formImageUploaderNewInitialState);

	const setImageOrientation = (value: TImageOrientation) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_IMAGE_ORIENTATION,
			payload: {
				imageOrientation: value,
			},
		});
	};

	const setLoading = (value: boolean) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_LOADING,
			payload: {
				isLoading: value,
			},
		});
	};

	const setUploadingVideo = (value: boolean) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_UPLOADING_VIDEO,
			payload: {
				isUploadingVideo: value,
			},
		});
	};

	const setPendingImage = (value: IPendingMedia) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_PENDING_IMAGE,
			payload: {
				pendingImage: value,
			},
		});
	};

	const setPendingGif = (value: IPendingMedia) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_PENDING_GIF,
			payload: {
				pendingGif: value,
			},
		});
	};

	const setPendingVideo = (value: IPendingMedia) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_PENDING_VIDEO,
			payload: {
				pendingVideo: value,
			},
		});
	};

	const setCroppedSizeSrc = (value: string | null) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_CROPPED_SIZE_SRC,
			payload: {
				src: value,
			},
		});
	};

	const setCommittedVideoSrc = (value: string | null) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_COMMITTED_VIDEO_SRC,
			payload: {
				src: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeTypeOfErrorModalWindow = (value: TTypeOfErrorModalWindow) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.CHANGE_TYPE_OF_ERROR_MODAL_WINDOW,
			payload: {
				typeOfErrorModalWindow: value,
			},
		});
	};

	const setLoadingImageError = (value: boolean) => {
		dispatch({
			type: FormImageUploaderNewActionTypes.SET_LOADING_IMAGE_ERROR,
			payload: {
				loadingImageError: value,
			},
		});
	};

	const uploadMedia = async ({
		file,
		fileName,
		setUploading,
		setId,
		setSrc,
	}: {
		file: Blob;
		fileName: string;
		setUploading: (value: boolean) => void;
		setId: (fileId: TFileId) => void;
		setSrc: (value: string | null) => void;
	}) => {
		setUploading(true);
		const formData = new FormData();
		formData.append('file', file, fileName);
		const sourceUrl = URL.createObjectURL(file);
		try {
			const response = await postMediaFile({ data: formData });
			setId(response.data.fileId);
			setSrc(sourceUrl);
		} catch (err) {
			changeTypeOfErrorModalWindow('fileUploadError');
		} finally {
			setUploading(false);
		}
	};

	const uploadImage = async (image: Blob) => {
		const indexOfBlobTypeSeparator = image.type.indexOf('/');
		const fileExtension = image.type.slice(indexOfBlobTypeSeparator + 1);
		await uploadMedia({
			file: image,
			fileName: `image.${fileExtension}`,
			setUploading: setLoading,
			setId: setFileId,
			setSrc: setCroppedSizeSrc,
		});
	};

	const uploadVideo = async (video: File) => {
		if (!setVideoFileId) {
			return;
		}
		await uploadMedia({
			file: video,
			fileName: video.name,
			setUploading: setUploadingVideo,
			setId: setVideoFileId,
			setSrc: setCommittedVideoSrc,
		});
	};

	const keepExistingFile = () => {
		setFileId(null);
		setCroppedSizeSrc(null);
	};

	return (
		<FormImageUploaderNewContext.Provider
			value={{
				setImageOrientation,
				setLoading,
				setCroppedSizeSrc,
				setCommittedVideoSrc,
				setPendingImage,
				setPendingGif,
				setPendingVideo,
				changeTypeOfModalWindow,
				changeTypeOfErrorModalWindow,
				uploadImage,
				uploadVideo,
				keepExistingFile,
				setLoadingImageError,
				imageOrientation: state.imageOrientation,
				isLoading: state.isLoading,
				isUploadingVideo: state.isUploadingVideo,
				croppedSizeSrc: state.croppedSizeSrc,
				committedVideoSrc: state.committedVideoSrc,
				pendingImage: state.pendingImage,
				pendingGif: state.pendingGif,
				pendingVideo: state.pendingVideo,
				typeOfModalWindow: state.typeOfModalWindow,
				typeOfErrorModalWindow: state.typeOfErrorModalWindow,
				loadingImageError: state.loadingImageError,
			}}>
			{children}
		</FormImageUploaderNewContext.Provider>
	);
};
